/* eslint-disable react/prop-types */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { ThemeContext } from 'styled-components';

/**
 * Barra de pasos (scroll-spy) que indica en qué bloque está el usuario.
 *
 * No mide "completado" (los formularios traen valores por defecto), sino la
 * POSICIÓN actual: resalta el bloque visible y se "llena" hasta ese paso.
 * Al hacer clic en un paso, desplaza el scroll hasta el bloque correspondiente.
 *
 * Cada `step` debe tener: { key, title }, donde `key` es el `id` del SectionCard.
 */
const INACTIVE_DOT = '#d9d9d9';
const INACTIVE_LINE = '#ececec';

const SectionStepper = ({ steps }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const theme = useContext(ThemeContext);
  // Color primario del tema activo (morado, naranja, etc.); naranja por defecto.
  const ACTIVE = (theme && theme.primaryText) || '#D88F4F';

  useEffect(() => {
    // Busca el ancestro que realmente hace scroll (un contenedor con overflow
    // o, si no hay, la ventana). Necesario para detectar el fondo.
    const getScrollParent = (node) => {
      let el = node && node.parentElement;
      while (el) {
        const oy = window.getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
          return el;
        }
        el = el.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };

    const handler = () => {
      // Si el stepper (o su pestaña) está oculto, no medimos: los rects valen 0
      // y darían un paso activo falso (toda la barra llena).
      if (!ref.current || ref.current.offsetParent === null) return;
      // Trabajamos en coordenadas de viewport: como el stepper es "sticky",
      // su borde inferior marca la línea fija contra la que comparamos cada
      // bloque. Así funciona sin importar si el scroll lo hace la ventana o
      // un contenedor interno.
      const line = ref.current.getBoundingClientRect().bottom + 16;
      let current = 0;
      let best = Infinity;
      steps.forEach((s, i) => {
        const el = document.getElementById(s.key);
        const rect = el && el.getBoundingClientRect();
        // Marca la sección visible cuyo inicio esté más cerca de la línea.
        if (rect && rect.height > 0) {
          const dist = Math.abs(rect.top - line);
          if (dist < best) {
            best = dist;
            current = i;
          }
        }
      });
      // Las últimas secciones (cortas) no alcanzan a subir hasta la línea;
      // si llegamos al fondo del scroll, marcamos el último paso.
      const scroller = getScrollParent(ref.current);
      if (scroller && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) {
        current = steps.length - 1;
      }
      setActive(current);
    };

    handler();
    // Captura (3.er argumento true) para escuchar el scroll tanto de la ventana
    // como de cualquier contenedor interno con overflow.
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);

    // Recalcula cuando el stepper se vuelve visible (p. ej. al abrir la pestaña),
    // ya que en ese momento no se dispara ningún evento de scroll.
    let io;
    if (ref.current && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(() => handler());
      io.observe(ref.current);
    }

    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
      if (io) io.disconnect();
    };
    // eslint-disable-next-line
  }, [steps]);

  const goTo = (key) => {
    const el = document.getElementById(key);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      ref={ref}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        background: '#fff',
        padding: '12px 8px 10px',
        marginBottom: 12,
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto' }}>
        {steps.map((s, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={s.key}>
            {i > 0 && (
              <div
                style={{
                  flex: 1,
                  minWidth: 16,
                  height: 2,
                  marginTop: 7,
                  background: i <= active ? ACTIVE : INACTIVE_LINE,
                }}
              />
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={() => goTo(s.key)}
              onKeyPress={() => goTo(s.key)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '0 4px',
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: i <= active ? ACTIVE : '#fff',
                  border: `2px solid ${i <= active ? ACTIVE : INACTIVE_DOT}`,
                  display: 'inline-block',
                  transition: 'all .2s',
                }}
              />
              <span
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  color: i === active ? ACTIVE : '#8c8c8c',
                  fontWeight: i === active ? 600 : 400,
                }}
              >
                {s.title}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default SectionStepper;
